import pages from "@/assets/pages.json";
import { FaUniversity } from "react-icons/fa";
import { IoBoat, IoBook } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { SearchActions } from "./sagas/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ExternalLink, Search } from "lucide-react";

const renderIcon = (name: string) => {
    switch (name) {
        case 'IoBoat': return <IoBoat />
        case 'IoBook': return <IoBook />
        case 'FaUniversity': return <FaUniversity />
    }
}

/** Top-level entries that carry a submenu — "Home" is covered by the wordmark. */
const sections = (pages as any[]).filter((p) => Array.isArray(p.items) && p.items.length > 0);

/** Swagger UI served by the backend, e.g. https://scienceportal.nioz.nl/api/v1/docs */
const apiDocsUrl = `${import.meta.env.VITE_API_URL}/docs`;

const linkClass = (active: boolean) =>
    [
        'flex items-center gap-1.5 px-3 py-1.5 text-[15px] font-medium cursor-pointer',
        'border-b-2 transition-colors duration-150 bg-transparent',
        active
            ? 'text-white border-warm-fill'
            : 'text-chrome-ink border-transparent hover:text-white hover:border-warm-fill',
    ].join(' ');

const Menu = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Close the mega-menu whenever the route changes or Escape is pressed.
    useEffect(() => setOpenMenu(null), [location.pathname]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const handleNav = (slug: string) => {
        dispatch({ type: SearchActions.SET_URL, payload: slug })
        dispatch({ type: SearchActions.RESET_SEARCH })
        navigate(slug)
        setOpenMenu(null)
    }

    const cancelClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }

    const scheduleClose = () => {
        cancelClose();
        closeTimeoutRef.current = setTimeout(() => setOpenMenu(null), 150);
    }

    const isResults = location.pathname.startsWith('/results') || location.pathname.startsWith('/search');

    return (
        <nav className="flex flex-wrap items-center gap-0.5" aria-label="Main">
            <button
                type="button"
                className={linkClass(isResults)}
                onClick={() => navigate('/results')}
                onMouseEnter={() => setOpenMenu(null)}
            >
                <Search className="h-4 w-4 opacity-70" />
                Search data
            </button>

            {sections.map((menuItem: any) => {
                const open = openMenu === menuItem.name;
                return (
                    <div key={menuItem.name} className="relative">
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-haspopup="true"
                            className={linkClass(open)}
                            onMouseEnter={() => { cancelClose(); setOpenMenu(menuItem.name); }}
                            onMouseLeave={scheduleClose}
                            onClick={() => setOpenMenu(open ? null : menuItem.name)}
                        >
                            {menuItem.name}
                            <ChevronDown
                                className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {open && (
                            <div
                                className="fixed left-0 right-0 z-[70] flex justify-center px-4"
                                style={{ top: 'calc(var(--nz-header-h) + 3px)', pointerEvents: 'none' }}
                            >
                                <div
                                    className="nz-card nz-scroll w-full max-w-[900px] overflow-y-auto p-4 shadow-float animate-[fadeup_.16s_ease_both]"
                                    style={{ pointerEvents: 'auto', maxHeight: 'calc(100vh - var(--nz-header-h) - 40px)' }}
                                    onMouseEnter={cancelClose}
                                    onMouseLeave={() => setOpenMenu(null)}
                                >
                                    {menuItem.description && (
                                        <p className="mb-4 border-l-2 border-brand-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
                                            {menuItem.description}
                                        </p>
                                    )}

                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                                        {menuItem.items.map((item: any) => (
                                            <button
                                                type="button"
                                                key={item.slug || item.name}
                                                className="nz-card nz-card-hover flex flex-col gap-2 p-3 text-left"
                                                onClick={() => handleNav(item.slug ? `/page/${item.slug}` : '#')}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
                                                        {renderIcon(item.icon)}
                                                    </span>
                                                    <span className="text-sm font-semibold text-ink">{item.name}</span>
                                                </div>
                                                <p className="line-clamp-3 text-xs leading-relaxed text-ink-3">
                                                    {item.description}
                                                </p>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}

            <a
                href={apiDocsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass(false)}
                onMouseEnter={() => setOpenMenu(null)}
            >
                API
                <ExternalLink className="h-3 w-3" />
            </a>
        </nav>
    )
}

export default Menu;
